import { DangerButton, PrimaryButton } from '@shared/components/buttons';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useState } from 'react';
import { Loading } from '../../components/loading';
import { Page } from '../../components/page';
import { StoreOperation, unavailableReason } from '../../data/permissions';
import { formatShortDate } from '../../helpers/format-date';
import { useBulkDeleteOrders, useCreateOrder, useDraftOrders } from '../../hooks/orders';
import { useVendor } from '../../hooks/vendors';
import { orderCleanupRoute, storeLayout } from '../../route-apis';

export function OrderCleanup() {
  const { vendorId } = orderCleanupRoute.useSearch();
  const { vendor } = useVendor(vendorId);
  const { store } = storeLayout.useRouteContext();

  // Cancelling is owner/manager only on the API side. Without this check the
  // page offers an employee a button that can only ever answer 403, which is
  // how a store owner spent half an hour thinking her account was broken.
  const cancelUnavailableReason = unavailableReason(store, StoreOperation.CancelOrder);
  const bulkDeleteOrders = useBulkDeleteOrders();
  const { draftOrdersByVendor } = useDraftOrders();
  const draftOrders = draftOrdersByVendor(vendorId);
  const [loading, setLoading] = useState(false);

  const createOrder = useCreateOrder();
  const navigate = useNavigate();

  const navigateToOrder = useCallback(
    (orderId: string) => {
      navigate({
        to: '/stores/$storeId/orders/$orderId/edit',
        params: { storeId: store.id, orderId },
      });
    },
    [navigate, store]
  );

  const deleteExistingOrdersAndCreateNew = useCallback(() => {
    setLoading(true);
    bulkDeleteOrders(draftOrders)
      .then(() => {
        createOrder(store.id, { vendor_id: vendorId }).then((order) => {
          navigateToOrder(order.id);
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [store, vendorId, draftOrders, createOrder, navigateToOrder, bulkDeleteOrders]);

  const openMostRecentOrder = useCallback(() => {
    const mostRecentOrder = draftOrders[0];
    navigateToOrder(mostRecentOrder.id);
  }, [draftOrders, navigateToOrder]);

  if (draftOrders.length === 0 || loading) {
    return <Loading />;
  }

  const single = draftOrders.length === 1;
  const vendorName = vendor?.name || 'this vendor';
  const updatedOn = formatShortDate(new Date(draftOrders[0].updated_at));
  const openDescription = single
    ? `Continue with the ${vendorName} order updated ${updatedOn}.`
    : `Continue with the most recent of the ${draftOrders.length} ${vendorName} draft orders (updated ${updatedOn}).`;
  const createDescription = cancelUnavailableReason
    ? `${cancelUnavailableReason} Ask an owner of ${store.name} to cancel ${single ? 'it' : 'them'} for you.`
    : `Discard the ${single ? 'existing order' : `${draftOrders.length} existing orders`} and create a new one. Any updates to the existing order(s) will be lost.`;

  return (
    <Page title="Existing Order Found">
      <div className="flex flex-col gap-4 max-w-2xl mx-auto pt-10">
        <ActionPanel title="Open Existing" description={openDescription}>
          <PrimaryButton onClick={openMostRecentOrder}>Open Existing</PrimaryButton>
        </ActionPanel>
        <ActionPanel title="Cancel &amp; Create New" description={createDescription}>
          <DangerButton
            onClick={deleteExistingOrdersAndCreateNew}
            disabledReason={cancelUnavailableReason}
          >
            Cancel &amp; Create New
          </DangerButton>
        </ActionPanel>
      </div>
    </Page>
  );
}

function ActionPanel({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-gray-50 rounded-md">
      <div className="p-4 sm:p-5">
        <div className="flex justify-between items-start gap-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">{title}</h3>
            <div className="mt-2 text-sm text-gray-500">
              <p>{description}</p>
            </div>
          </div>
          <div className="flex-shrink-0">{children}</div>
        </div>
      </div>
    </div>
  );
}
